/**
 * LiquiditySummary (#1315) — the plain-language header so a farmer who glances
 * at the page immediately knows the situation, without reading the chart:
 *   1. hero  — the current balance ("what do I have right now") — since #3148
 *      only for tenants WITHOUT a per-account breakdown (single-account): for
 *      them it is the page's only Kontostand figure. Breakdown tenants read
 *      their balances off the "Alle Konten — Summe" row instead, so the hero
 *      duplicated it and is gone; the +Neu ``headerAction`` moved onto the
 *      verdict row.
 *   2. risk line — the refined Ampel verdict (day-precise breach + lowest point
 *      + KK-limit overrun), via VerdictLine. Hidden for the neutral
 *      "KK-Limit noch nicht hinterlegt" verdict (#3148 d — discriminated by
 *      ``verdict.kkLimitEur == null``, the one shape with a null limit).
 * The Ist→Prognose orientation caption lives under the chart, not here.
 */
import type { ReactNode } from 'react';
import { useCollapsiblePersisted } from '../../../hooks/useCollapsiblePersisted';
import { formatEur } from '../../../utils/formatEur';
import type { StatementResidual, Verdict } from './types';
import type { LiquiditySummary as Summary } from './summary';
import VerdictLine from './VerdictLine';
import './LiquiditySummary.css';

/**
 * #3036 — persisted fold preference for the per-account balance list. A NEW key
 * (the `hofsync.<area>.<pref>` shape), so the default-flip hazard documented at
 * `useCollapsiblePersisted.ts:83-86` does not apply: nothing has ever stored it,
 * so `defaultFolded = true` reaches everyone rather than only untouched users.
 */
export const ACCOUNT_BREAKDOWN_FOLD_KEY = 'hofsync.liquiditaetsplanung.accountBreakdownFolded';

const longDate = (iso: string): string =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString('de-DE', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });

/** ``DD.MM.YYYY`` — the compact form for the HOF-5 as-of qualifier, which shares
 *  a line with the balance it qualifies and cannot afford a spelled-out month.
 *  The field widths are explicit: a bare de-DE format renders ``31.7.2026`` on
 *  some runtimes and ``31.07.2026`` on others, and this sits next to a
 *  tabular-nums figure where the ragged form is visibly wrong. */
const shortDate = (iso: string): string =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'UTC',
  });

/** Day-precise red risk line; otherwise the verdict's own sentence (yellow/green
 *  and red-without-a-breach stay as computed). */
function riskSentence(verdict: Verdict, summary: Summary): string | undefined {
  if (verdict.level === 'red' && summary.breachDate && summary.overrunEur != null) {
    return (
      `Engpass ab ${longDate(summary.breachDate)} – tiefster Stand ` +
      `${formatEur(summary.lowestForecastEur)}, überschreitet das KK-Limit um ` +
      `${formatEur(summary.overrunEur)}.`
    );
  }
  return undefined; // VerdictLine falls back to verdict.sentence
}

/** One row of the Alle-Konten per-account balance breakdown (#2005). */
export interface AccountBreakdownRow {
  id: string;
  label: string;
  color: string;
  balanceEur: number | null;
}

export default function LiquiditySummary({
  verdict,
  summary,
  headerAction,
  breakdown,
  selectedId,
  onSelectAccount,
  asOfDate,
  asOfIsForecast = false,
  truncated = false,
  reconciled = null,
  statementResiduals = [],
}: {
  verdict: Verdict;
  summary: Summary;
  /** Optional action rendered on the header row's right (e.g. the +Neu button):
   *  the hero row for a no-breakdown tenant, the verdict row otherwise (#3148). */
  headerAction?: ReactNode;
  /** Per-account balance rows for the Alle Konten scope (#2005). */
  breakdown?: AccountBreakdownRow[];
  /** HOF-5 — the ISO date the breakdown balances are read at, when that is NOT
   *  today (i.e. the farmer selected another period). Passed only then, so the
   *  default view gains no chrome; rendered as a chip qualifying the figures,
   *  since a balance with no date is read as "right now". */
  asOfDate?: string;
  /** HOF-5 — that date sits past the Ist horizon, so the figures are projected.
   *  Says „Prognose" instead of „Stand": the money is not banked yet. */
  asOfIsForecast?: boolean;
  /** #2160 — the account currently in scope (its ``?konto=`` id). Only meaningful
   *  alongside ``onSelectAccount``. */
  selectedId?: string;
  /** #2160 — when provided (multi-account), the breakdown rows fold in the account
   *  switcher: each row becomes an ``aria-pressed`` button that selects its account.
   *  Absent ⇒ static rows, character-identical to before (#2089 stays green). */
  onSelectAccount?: (id: string) => void;
  /** HOF-97 criterion 7 — the bank feed hit the ``MAX_PAGES 10 x PAGE_LIMIT 1000`` cap
   *  (#2611, second instance: WF has 14.946 rows and reads return 10.000), so bookings
   *  are missing from the figure above. ``BalanceChart`` has disclosed this since #2611;
   *  the tile carrying the number the customer actually quoted did not. */
  truncated?: boolean;
  /** HOF-97 §6 L5.4 — did the balance tie out to the bank's own Endzeilen? Three-valued:
   *  ``false`` is a MEASURED defect, ``true`` is clean, and **``null`` means never
   *  checked**, which renders nothing. An account with no Endzeile has not been found
   *  wanting — it has not been looked at — and claiming a defect there would cry wolf on
   *  every tenant whose bank documents are not ingested yet. */
  reconciled?: boolean | null;
  /** The per-period residuals behind ``reconciled: false``. Their magnitudes are what
   *  turn "something is off" into a euro figure a farmer can go and look for. */
  statementResiduals?: StatementResidual[];
}) {
  const { currentBalanceEur } = summary;
  const negative = currentBalanceEur != null && currentBalanceEur < 0;
  // The breakdown doubles as the account switcher only when a select handler is
  // wired (multi-account). Each row's dot/label/value content is shared between
  // the static <li> and the interactive <button>, so the dotted-leader CSS holds.
  const interactive = Boolean(onSelectAccount);
  // #3036 — the list folds to one line so the balance chart (the reason most
  // people open the page) is not pushed below the viewport fold on a ~11-account
  // tenant. Fold and button share ONE gate, so a folded panel can never appear
  // without the control that reopens it. That gate includes `interactive`
  // because a non-switcher breakdown only exists for a single-account tenant —
  // and `useLiquiditaetsplanung` returns `accountSeries: undefined` for
  // `accounts.length <= 1`, so in production that path renders no list at all.
  const rows = breakdown ?? [];
  // #3148 (a) — the hero renders ONLY when no breakdown is present. With a
  // breakdown, the "Alle Konten — Summe" row is the balance surface and the hero
  // duplicated it (via a DIFFERENT derivation — liquiditySummary() vs
  // balanceAtToday() — whose rare visible mismatch the issue calls out). Without
  // one (accounts.length <= 1 ⇒ accountSeries undefined) the hero is the page's
  // ONLY Kontostand, so removing it unconditionally would blank that tenant.
  const hasBreakdown = rows.length > 0;
  // HOF-97 criterion 7 — the figure above discloses when it is not the whole story.
  //
  // The two signals share ONE notice rather than stacking two, because they are one
  // message to the farmer: *this number is incomplete*. Which mechanism caused it is an
  // operator's question, and two competing caveats on a headline figure read as noise
  // and get ignored — which is how the #2611 chart note ended up not preventing this
  // issue in the first place.
  //
  // Residuals are summed by MAGNITUDE. A +50.000 under-extraction on one Konto and a
  // -50.000 over-extraction on another are two defects, not zero; a signed sum would
  // report a clean tile over 100.000 € of error, which is exactly the silent
  // cancellation this whole issue is about. (§4: the five defects "compound rather than
  // cancel" — but only because nobody was adding them up either way.)
  const residualTotalEur = statementResiduals.reduce((sum, r) => sum + Math.abs(r.residualEur), 0);
  const incompletenessNote =
    truncated || reconciled === false ? (
      <p className="liquiditaetsplanung-summary__incompleteness" role="status" data-testid="liquiditaetsplanung-incompleteness">
        {truncated
          ? 'Unvollständige Daten: es konnten nicht alle Buchungen geladen werden. '
          : ''}
        {reconciled === false
          ? `Der Saldo stimmt nicht mit den Endzeilen der Kontoauszüge überein — Abweichung ${formatEur(residualTotalEur)}.`
          : ''}
      </p>
    ) : null;
  // #3148 (d) — the neutral "KK-Limit noch nicht hinterlegt" verdict is the only
  // verdict shape with a null limit; a genuine tight-month verdict shares the
  // word "Eng" but always carries a number. Suppressed render-side: buildVerdict
  // stays untouched (its sentence is pinned by computeBalanceSeries.test.ts /
  // aggregateSeries.test.ts, and its nulls feed null-guarded consumers).
  const showVerdict = verdict.kkLimitEur != null;
  const { folded, toggle } = useCollapsiblePersisted(ACCOUNT_BREAKDOWN_FOLD_KEY, true);
  const foldable = interactive && rows.length > 1;
  const collapsed = foldable && folded;
  /**
   * HOF-5 — the scope row is PINNED to the control bar and stays there whether
   * the list is folded or not.
   *
   * It used to live inside the list, which meant unfolding MOVED it: the one
   * line a farmer reads the farm's balance off jumped out from beside the
   * „Konten" pill and down into the list, taking the number with it. Pinning it
   * makes the bar's geometry independent of the fold — the balance is always in
   * the same place — and it is what lets the whole header sit on one line.
   *
   * It shows the row for the scope currently in view, NOT unconditionally the
   * "Alle Konten — Summe" aggregate: with `?konto=<id>` set, the aggregate would
   * contradict the account the rest of the page is scoped to (#2160).
   *
   * Pinned only where there is a fold to survive: a breakdown with no fold
   * control (a static list, or a single row) never hid its rows in the first
   * place, so hoisting one into the bar would split a list for no gain and
   * reorder it. Those render exactly as before — every row in the list below.
   */
  const pinnedRow = foldable ? rows.find((r) => r.id === selectedId) ?? rows[0] : undefined;
  /** The list carries everything the pinned line does not — so no row is
   *  rendered twice and none is unreachable. Natural order is preserved. */
  const listRows = collapsed ? [] : rows.filter((r) => r.id !== pinnedRow?.id);
  const rowContent = (row: AccountBreakdownRow) => (
    <>
      <span
        className="liquiditaetsplanung-summary__breakdown-dot"
        style={{ background: row.color }}
        aria-hidden="true"
      />
      <span className="liquiditaetsplanung-summary__breakdown-label">{row.label}</span>
      <span className="liquiditaetsplanung-summary__breakdown-value">
        {row.balanceEur == null ? '—' : formatEur(row.balanceEur)}
      </span>
    </>
  );
  const verdictLine = showVerdict ? (
    <VerdictLine verdict={verdict} sentence={riskSentence(verdict, summary)} />
  ) : null;
  /** HOF-5 — the date qualifier on the balances, rendered only when they are NOT
   *  read at today (see the prop docs). */
  const asOfChip = asOfDate ? (
    <span
      className={`liquiditaetsplanung-summary__asof${
        asOfIsForecast ? ' liquiditaetsplanung-summary__asof--forecast' : ''
      }`}
    >
      {asOfIsForecast ? 'Prognose' : 'Stand'} {shortDate(asOfDate)}
    </span>
  ) : null;
  return (
    <div className="liquiditaetsplanung-summary">
      {!hasBreakdown && (
        <div className="liquiditaetsplanung-summary__top">
          <div className="liquiditaetsplanung-summary__now">
            <span className="liquiditaetsplanung-summary__now-label">
              {asOfDate ? `Kontostand ${shortDate(asOfDate)}` : 'Aktueller Kontostand'}
            </span>
            <span
              className={`liquiditaetsplanung-summary__now-value${
                negative ? ' liquiditaetsplanung-summary__now-value--negative' : ''
              }`}
            >
              {currentBalanceEur == null ? '—' : formatEur(currentBalanceEur)}
            </span>
          </div>
          {headerAction}
        </div>
      )}
      {!hasBreakdown && verdictLine}
      {/* HOF-5 — ONE control bar for the breakdown layout: the fold pill, the
          pinned scope balance, the verdict and the +Neu action all on a single
          line. Until now the verdict+action sat on one row and the pill+balance
          on another, and the balance MOVED when the list unfolded. The bar's
          contents are fixed; only the list below it appears and disappears. */}
      {hasBreakdown && (
        <div className="liquiditaetsplanung-summary__bar">
          {/* The control renders BEFORE the body it discloses, so the keyboard
              reaches the toggle before the list it opens. Mirrors
              MarginRankChart.tsx:374-385: an aria-hidden caret for anyone who
              reads shape faster than state, a visible text label carrying the
              accessible name, and `aria-pressed` in the "the list is showing"
              sense — which is exactly why the name must NOT swap with state. */}
          {foldable && (
            <button
              type="button"
              className="liquiditaetsplanung-summary__fold"
              aria-pressed={!folded}
              onClick={toggle}
            >
              <span className="liquiditaetsplanung-summary__fold-caret" aria-hidden="true">
                {folded ? '▸' : '▾'}
              </span>
              Konten
            </button>
          )}
          {pinnedRow && (
            // Keeps ``__breakdown-row`` as well: hoisting the row out of the
            // list changes WHERE it sits, not WHAT it is, so it keeps the row
            // styling and stays findable as one of the breakdown's rows.
            <div className="liquiditaetsplanung-summary__breakdown-row liquiditaetsplanung-summary__pinned">
              {interactive ? (
                <button
                  type="button"
                  className="liquiditaetsplanung-summary__breakdown-button"
                  aria-pressed={pinnedRow.id === selectedId}
                  onClick={() => onSelectAccount!(pinnedRow.id)}
                >
                  {rowContent(pinnedRow)}
                </button>
              ) : (
                rowContent(pinnedRow)
              )}
            </div>
          )}
          {asOfChip}
          {verdictLine}
          {headerAction}
        </div>
      )}
      {incompletenessNote}
      {hasBreakdown && listRows.length > 0 && (
        <ul
          className="liquiditaetsplanung-summary__breakdown"
          {...(interactive ? { role: 'group', 'aria-label': 'Konto' } : {})}
        >
          {listRows.map((row) => (
            <li key={row.id} className="liquiditaetsplanung-summary__breakdown-row">
              {interactive ? (
                <button
                  type="button"
                  className="liquiditaetsplanung-summary__breakdown-button"
                  aria-pressed={row.id === selectedId}
                  onClick={() => onSelectAccount!(row.id)}
                >
                  {rowContent(row)}
                </button>
              ) : (
                rowContent(row)
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
